import type { MonthlyShiftSlotSummaryRepository } from './ports/MonthlyShiftSlotSummaryRepository';

/**
 * 月次シフト枠削除UseCaseの入力パラメータ
 *
 * @property {string} id - 削除対象の月次シフト枠ID
 */
export interface DeleteMonthlyShiftSlotInput {
  id: string;
}

/**
 * 月次シフト枠削除UseCaseの出力結果
 *
 * @property {boolean} success - 削除成功フラグ
 * @property {string[]} [errors] - エラーメッセージ配列（失敗時のみ）
 */
export interface DeleteMonthlyShiftSlotOutput {
  success: boolean;
  errors?: string[];
}

/**
 * 月次シフト枠を削除するUseCase
 *
 * @description
 * 指定されたIDの月次シフト枠を削除する。
 * 削除可否の判定はバックエンドAPI側で行い、確定済み予約がある場合など
 * 削除不可の場合はAPIがエラーを返す。
 *
 * @param {MonthlyShiftSlotSummaryRepository} repository - Repository実装（DI）
 * @param {DeleteMonthlyShiftSlotInput} input - 入力パラメータ
 * @returns {Promise<DeleteMonthlyShiftSlotOutput>} 削除結果
 *
 * @example
 * const result = await deleteMonthlyShiftSlot(repository, { id: 'uuid-xxx' });
 * if (result.success) {
 *   console.log('削除成功');
 * } else {
 *   console.error(result.errors?.[0]); // エラーメッセージ
 * }
 */
export async function deleteMonthlyShiftSlot(
  repository: MonthlyShiftSlotSummaryRepository,
  input: DeleteMonthlyShiftSlotInput
): Promise<DeleteMonthlyShiftSlotOutput> {
  try {
    // 削除実行（削除可否判定はバックエンド側で実施）
    await repository.delete(input.id);
    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : '削除に失敗しました';
    return {
      success: false,
      errors: [errorMessage]
    };
  }
}
